import { Component, inject } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePropPipe } from '../../pipes/translate-prop.pipe';

interface LocalizedText {
  nameKz: string;
  nameRu: string;
  nameQl: string;
  nameEn: string;
}

interface UpdateMessages {
  title: LocalizedText;
  introHighlight: LocalizedText;
  intro: LocalizedText;
  quickWay: LocalizedText;
  ctrlF5Hint: LocalizedText;
  ifPersists: LocalizedText;
  clearCacheInstructions: LocalizedText;
  mobileHint: LocalizedText;
  cacheLabel: LocalizedText;
  cacheExplain: LocalizedText;
  actionReload: LocalizedText;
  actionUnderstood: LocalizedText;
}

@Component({
  selector: 'app-build-update-dialog',
  templateUrl: './build-update-dialog.component.html',
  styleUrls: ['./build-update-dialog.component.scss'],
  imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslatePropPipe],
})
export class BuildUpdateDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<BuildUpdateDialogComponent>);

  readonly messages: UpdateMessages = {
    title: {
      nameKz: 'Назар аударыңыз!',
      nameRu: 'Внимание!',
      nameQl: 'Nazar audaryńyz!',
      nameEn: 'Attention!',
    },
    introHighlight: {
      nameKz: 'Портал жаңартылды.',
      nameRu: 'Портал был обновлён.',
      nameQl: 'Portal jańartyldy.',
      nameEn: 'The portal has been updated.',
    },
    intro: {
      nameKz:
        'Браузер беттің ескі нұсқасын көрсетуі мүмкін — бұл қателерге немесе ақпараттың дұрыс көрсетілмеуіне әкелуі мүмкін.',
      nameRu:
        'Браузер может показывать старую версию страницы — из-за этого могут возникать ошибки или некорректное отображение информации.',
      nameQl:
        'Brauzer bettiń esgi nusqasyn körsete alady — bul qatelerge nemese aqparattyn durys körsetilmeuine akeluі múmkin.',
      nameEn:
        'Your browser may be showing an older version of the page, which can cause errors or display issues.',
    },
    quickWay: {
      nameKz: 'Ең жылдам тәсіл:',
      nameRu: 'Самый быстрый способ:',
      nameQl: 'Eń jyldam tásil:',
      nameEn: 'Quickest fix:',
    },
    ctrlF5Hint: {
      nameKz:
        'Ctrl + F5 пернелер тіркесімін басыңыз (Mac-та: Cmd ⌘ + Shift + R) — бет сақталған деректерді пайдаланбай жаңартылады.',
      nameRu:
        'Нажмите Ctrl + F5 (на Mac: Cmd ⌘ + Shift + R) — страница обновится без использования сохранённых данных.',
      nameQl:
        'Ctrl + F5 basıńyz (Mac-ta: Cmd ⌘ + Shift + R) — bet saqtalǵan derekterdi paydalanbaı jańartylady.',
      nameEn: 'Press Ctrl + F5 (on Mac: Cmd ⌘ + Shift + R) — this reloads the page without using cached data.',
    },
    ifPersists: {
      nameKz: 'Мәселе жалғасса:',
      nameRu: 'Если проблема сохраняется:',
      nameQl: 'Mäsele jalǵassa:',
      nameEn: 'If the problem persists:',
    },
    clearCacheInstructions: {
      nameKz: 'Браузер кэшін тазалаңыз: Параметрлер → Тарих → Кэшті тазалау',
      nameRu: 'Очистите кэш браузера: Настройки → История → Очистить кэш',
      nameQl: 'Brauzer keshin tazalaıńyz: Parametrler → Tarıh → Keshti tazalau',
      nameEn: 'Clear your browser cache: Settings → History → Clear cache',
    },
    mobileHint: {
      nameKz: 'Мобильді құрылғыда: қойындыны жабып, порталды қайта ашыңыз.',
      nameRu: 'На мобильном телефоне: просто закройте вкладку и откройте портал снова.',
      nameQl: 'Mobildi qurylǵyda: qoıyndyny jabıp, portaldı qaıta ashyńyz.',
      nameEn: 'On mobile: close the tab and reopen the portal.',
    },
    cacheLabel: {
      nameKz: 'Кэш дегеніміз не?',
      nameRu: 'Что такое кэш?',
      nameQl: 'Kesh degenimiz ne?',
      nameEn: 'What is cache?',
    },
    cacheExplain: {
      nameKz:
        'Кэш — браузер беттердің жылдам ашылуы үшін сақтайтын уақытша деректер. Портал жаңартылғаннан кейін бұл деректерді тазалау қажет болуы мүмкін.',
      nameRu:
        'Кэш — это временные данные, которые браузер сохраняет, чтобы страницы открывались быстрее. После обновления портала может потребоваться очистить эти данные.',
      nameQl:
        'Kesh — brauzer betterdiń jyldam ashyluı úshin saqtaıtyn uaqytsha derekter. Portal jańartylǵannan keıin bul derekterdi tazalaý qajet boluy múmkin.',
      nameEn: 'Cache is temporary data that browsers save to load pages faster. After a portal update, you may need to clear this data.',
    },
    actionReload: {
      nameKz: 'Бетті жаңарту',
      nameRu: 'Обновить страницу',
      nameQl: 'Betti jańartu',
      nameEn: 'Reload Page',
    },
    actionUnderstood: {
      nameKz: 'Түсінікті',
      nameRu: 'Понятно',
      nameQl: 'Túsinikti',
      nameEn: 'Got it',
    },
  };

  close(): void {
    this.dialogRef.close();
  }

  reloadPage(): void {
    this.dialogRef.close();
    window.location.reload();
  }
}
